import { createReducer, on } from '@ngrx/store';
import { AuthActions } from './auth.actions';
import { IAuthUser } from '@core/interfaces/auth-user.interface';
import {
  IApplicationSession,
  ANONYMOUS_SESSION,
} from '@core/services/auth/access-claims';

export interface IAuthState extends IApplicationSession {
  init: boolean;
  currentUser: IAuthUser | null;
  profileStatus: 'idle' | 'loading' | 'ready' | 'failed' | 'forbidden';
  favoriteUrl: string | null;
  error: string | null;
}

export const initialState: IAuthState = {
  ...ANONYMOUS_SESSION,
  init: false,
  currentUser: null,
  profileStatus: 'idle',
  favoriteUrl: null,
  error: null,
};

export const authReducer = createReducer(
  initialState,
  on(AuthActions.initEnd, (state, { favoriteUrl }) => ({
    ...state,
    init: true,
    favoriteUrl,
  })),
  on(AuthActions.sessionChanged, (state, { session }): IAuthState => {
    const needsProfile =
      session.authenticated && !session.isSecurityAdministrator;
    const preserveProfile =
      needsProfile && state.authenticated && !state.isSecurityAdministrator;
    return {
      ...state,
      ...session,
      currentUser: preserveProfile ? state.currentUser : null,
      profileStatus: preserveProfile
        ? state.profileStatus
        : needsProfile
          ? 'loading'
          : 'idle',
      error: preserveProfile ? state.error : null,
    };
  }),
  on(
    AuthActions.sessionFailed,
    (state, { message }): IAuthState => ({
      ...state,
      ...ANONYMOUS_SESSION,
      currentUser: null,
      profileStatus: 'idle',
      error: message,
    }),
  ),
  on(AuthActions.setFavoriteUrl, (state, { favoriteUrl }) => ({
    ...state,
    favoriteUrl,
  })),
  on(
    AuthActions.requestCurrentUser,
    (state): IAuthState => ({
      ...state,
      profileStatus: 'loading',
      error: null,
    }),
  ),
  on(
    AuthActions.requestCurrentUserSuccess,
    (state, { user }): IAuthState => ({
      ...state,
      currentUser: user,
      profileStatus: 'ready',
      error: null,
    }),
  ),
  on(
    AuthActions.requestCurrentUserError,
    (state, { message, denied }): IAuthState => ({
      ...state,
      currentUser: null,
      profileStatus: denied ? 'forbidden' : 'failed',
      error: message,
    }),
  ),
);
